import { Sparkles, Lightbulb, Sun, Droplets, Shield, ThermometerSun, TrendingUp, HelpCircle, Megaphone, Search } from 'lucide-react';
import { SectionPageEditor } from '@/components/admin/SectionPageEditor';
import type { SectionDef } from '@/components/admin/SectionPageEditor';

// All 9 sections match pages/3-step-treatment/page.tsx's render order — all
// 9 component files there are live (no dead files, unlike Home). 95 of the
// 98 threeStep.* keys in the locale bundle are used; the 3 orphaned
// step0N.subtitle keys are excluded since no component reads them.
//
// Ingredient chip lists (Step 01/02/03) and the OPEN/TREAT/LOCK badge
// labels are hardcoded, non-i18n literals in the components — not
// editable here without first promoting them to real i18n keys.
const THREE_STEP_SECTIONS: SectionDef[] = [
  {
    key: 'hero',
    title: 'Hero',
    icon: Sparkles,
    images: [{ key: 'threeStep.hero.image', label: 'Hero Product Image' }],
    texts: [
      { key: 'threeStep.hero.label', label: 'Eyebrow Label' },
      { key: 'threeStep.hero.heading', label: 'Heading' },
      { key: 'threeStep.hero.subtitle', label: 'Subtitle' },
    ],
  },
  {
    key: 'philosophy',
    title: 'Why 3 Steps?',
    icon: Lightbulb,
    texts: [
      { key: 'threeStep.philosophy.label', label: 'Eyebrow Label' },
      { key: 'threeStep.philosophy.heading', label: 'Heading' },
      { key: 'threeStep.philosophy.p1', label: 'Paragraph 1', long: true },
      { key: 'threeStep.philosophy.prep', label: 'Prep — Title' },
      { key: 'threeStep.philosophy.prepDesc', label: 'Prep — Description' },
      { key: 'threeStep.philosophy.treat', label: 'Treat — Title' },
      { key: 'threeStep.philosophy.treatDesc', label: 'Treat — Description' },
      { key: 'threeStep.philosophy.seal', label: 'Seal — Title' },
      { key: 'threeStep.philosophy.sealDesc', label: 'Seal — Description' },
      { key: 'threeStep.philosophy.p2', label: 'Paragraph 2', long: true },
    ],
  },
  {
    key: 'step01',
    title: 'Step 01 — Skin Priming Booster Essence',
    icon: Sun,
    note: 'The ingredient chip list is fixed in code — only the text/image below are editable here.',
    images: [{ key: 'threeStep.step01.image', label: 'Product Photo' }],
    texts: [
      { key: 'threeStep.step01.badge', label: 'Step Badge' },
      { key: 'threeStep.step01.name', label: 'Product Name' },
      { key: 'threeStep.step01.price', label: 'Price' },
      { key: 'threeStep.step01.volume', label: 'Volume' },
      { key: 'threeStep.step01.desc', label: 'Description', long: true },
      { key: 'threeStep.step01.ingredientsLabel', label: 'Ingredients Label' },
      { key: 'threeStep.step01.howToUse', label: 'How to Use — Title' },
      { key: 'threeStep.step01.use1', label: 'How to Use — Step 1' },
      { key: 'threeStep.step01.use2', label: 'How to Use — Step 2' },
      { key: 'threeStep.step01.use3', label: 'How to Use — Step 3' },
      { key: 'threeStep.step01.use4', label: 'How to Use — Step 4' },
      { key: 'threeStep.step01.cta', label: 'Button' },
    ],
  },
  {
    key: 'step02',
    title: 'Step 02 — Daily Rescue Serum',
    icon: Droplets,
    note: 'The ingredient chip list is fixed in code — only the text/image below are editable here.',
    images: [{ key: 'threeStep.step02.image', label: 'Product Photo' }],
    texts: [
      { key: 'threeStep.step02.badge', label: 'Step Badge' },
      { key: 'threeStep.step02.name', label: 'Product Name' },
      { key: 'threeStep.step02.price', label: 'Price' },
      { key: 'threeStep.step02.volume', label: 'Volume' },
      { key: 'threeStep.step02.desc', label: 'Description', long: true },
      { key: 'threeStep.step02.ingredientsLabel', label: 'Ingredients Label' },
      { key: 'threeStep.step02.howToUse', label: 'How to Use — Title' },
      { key: 'threeStep.step02.use1', label: 'How to Use — Step 1' },
      { key: 'threeStep.step02.use2', label: 'How to Use — Step 2' },
      { key: 'threeStep.step02.use3', label: 'How to Use — Step 3' },
      { key: 'threeStep.step02.use4', label: 'How to Use — Step 4' },
      { key: 'threeStep.step02.use5', label: 'How to Use — Step 5' },
      { key: 'threeStep.step02.cta', label: 'Button' },
    ],
  },
  {
    key: 'step03',
    title: 'Step 03 — Barrier Seal Cream',
    icon: Shield,
    note: 'The ingredient chip list is fixed in code — only the text/image below are editable here.',
    images: [{ key: 'threeStep.step03.image', label: 'Product Photo' }],
    texts: [
      { key: 'threeStep.step03.badge', label: 'Step Badge' },
      { key: 'threeStep.step03.name', label: 'Product Name' },
      { key: 'threeStep.step03.price', label: 'Price' },
      { key: 'threeStep.step03.volume', label: 'Volume' },
      { key: 'threeStep.step03.desc', label: 'Description', long: true },
      { key: 'threeStep.step03.ingredientsLabel', label: 'Ingredients Label' },
      { key: 'threeStep.step03.howToUse', label: 'How to Use — Title' },
      { key: 'threeStep.step03.use1', label: 'How to Use — Step 1' },
      { key: 'threeStep.step03.use2', label: 'How to Use — Step 2' },
      { key: 'threeStep.step03.use3', label: 'How to Use — Step 3' },
      { key: 'threeStep.step03.use4', label: 'How to Use — Step 4' },
      { key: 'threeStep.step03.use5', label: 'How to Use — Step 5' },
      { key: 'threeStep.step03.cta', label: 'Button' },
    ],
  },
  {
    key: 'climate',
    title: "Adapted for Bangladesh's Climate",
    icon: ThermometerSun,
    texts: [
      { key: 'threeStep.climate.label', label: 'Eyebrow Label' },
      { key: 'threeStep.climate.heading', label: 'Heading' },
      { key: 'threeStep.climate.hot.title', label: 'Hot/Humid — Title' },
      { key: 'threeStep.climate.hot.subtitle', label: 'Hot/Humid — Subtitle' },
      { key: 'threeStep.climate.hot.tip1', label: 'Hot/Humid — Tip 1' },
      { key: 'threeStep.climate.hot.tip2', label: 'Hot/Humid — Tip 2' },
      { key: 'threeStep.climate.hot.tip3', label: 'Hot/Humid — Tip 3' },
      { key: 'threeStep.climate.hot.tip4', label: 'Hot/Humid — Tip 4' },
      { key: 'threeStep.climate.hot.tip5', label: 'Hot/Humid — Tip 5' },
      { key: 'threeStep.climate.cool.title', label: 'Cool/Dry — Title' },
      { key: 'threeStep.climate.cool.subtitle', label: 'Cool/Dry — Subtitle' },
      { key: 'threeStep.climate.cool.tip1', label: 'Cool/Dry — Tip 1' },
      { key: 'threeStep.climate.cool.tip2', label: 'Cool/Dry — Tip 2' },
      { key: 'threeStep.climate.cool.tip3', label: 'Cool/Dry — Tip 3' },
      { key: 'threeStep.climate.cool.tip4', label: 'Cool/Dry — Tip 4' },
      { key: 'threeStep.climate.cool.tip5', label: 'Cool/Dry — Tip 5' },
    ],
  },
  {
    key: 'timeline',
    title: 'Results Timeline',
    icon: TrendingUp,
    texts: [
      { key: 'threeStep.timeline.label', label: 'Eyebrow Label' },
      { key: 'threeStep.timeline.heading', label: 'Heading' },
      { key: 'threeStep.timeline.m1.period', label: 'Milestone 1 — Period' },
      { key: 'threeStep.timeline.m1.title', label: 'Milestone 1 — Title' },
      { key: 'threeStep.timeline.m1.desc', label: 'Milestone 1 — Description' },
      { key: 'threeStep.timeline.m2.period', label: 'Milestone 2 — Period' },
      { key: 'threeStep.timeline.m2.title', label: 'Milestone 2 — Title' },
      { key: 'threeStep.timeline.m2.desc', label: 'Milestone 2 — Description' },
      { key: 'threeStep.timeline.m3.period', label: 'Milestone 3 — Period' },
      { key: 'threeStep.timeline.m3.title', label: 'Milestone 3 — Title' },
      { key: 'threeStep.timeline.m3.desc', label: 'Milestone 3 — Description' },
    ],
  },
  {
    key: 'faq',
    title: 'FAQ',
    icon: HelpCircle,
    texts: [
      { key: 'threeStep.faq.label', label: 'Eyebrow Label' },
      { key: 'threeStep.faq.heading', label: 'Heading' },
      { key: 'threeStep.faq.q1', label: 'Question 1' },
      { key: 'threeStep.faq.a1', label: 'Answer 1', long: true },
      { key: 'threeStep.faq.q2', label: 'Question 2' },
      { key: 'threeStep.faq.a2', label: 'Answer 2', long: true },
      { key: 'threeStep.faq.q3', label: 'Question 3' },
      { key: 'threeStep.faq.a3', label: 'Answer 3', long: true },
      { key: 'threeStep.faq.q4', label: 'Question 4' },
      { key: 'threeStep.faq.a4', label: 'Answer 4', long: true },
      { key: 'threeStep.faq.q5', label: 'Question 5' },
      { key: 'threeStep.faq.a5', label: 'Answer 5', long: true },
    ],
  },
  {
    key: 'cta',
    title: 'Bundle CTA',
    icon: Megaphone,
    note: 'The "Shop Now" / "Where to Buy" button labels are shared site-wide strings, edited under Settings, not here.',
    texts: [
      { key: 'threeStep.cta.heading', label: 'Heading' },
      { key: 'threeStep.cta.price', label: 'Bundle Price' },
      { key: 'threeStep.cta.originalPrice', label: 'Original Price' },
      { key: 'threeStep.cta.save', label: 'Savings Label' },
      { key: 'threeStep.cta.desc', label: 'Description', long: true },
    ],
  },
  {
    key: 'seo',
    title: 'SEO',
    icon: Search,
    note: 'Shown in Google search results and social media link previews — not visible on the page itself.',
    texts: [
      { key: 'seo.threeStep.title', label: 'Meta Title', long: true },
      { key: 'seo.threeStep.description', label: 'Meta Description', long: true },
    ],
  },
];

export default function AdminThreeStepPage() {
  return <SectionPageEditor pageKey="threeStep" title="3-Step Treatment" sections={THREE_STEP_SECTIONS} />;
}
